import { CustomSplitType } from 'utils/types/navigation';
export class AmountSplit {
    static toEqualSplit(total, userIds, selectedIds) {
        if (!userIds?.length)
            return {};
        return this.toAmountSplit(total, userIds.reduce((acc, curr) => {
            acc[curr] = selectedIds?.includes(curr) ? '1' : '0';
            return acc;
        }, {}), selectedIds?.length || 0);
    }
    static toPercentageSplit(total, split) {
        const percentageSplit = {};
        const splitList = Object.entries(split);
        splitList.forEach(([userId, amount]) => {
            percentageSplit[Number(userId)] = Number(((Math.abs(Number(amount)) / Math.abs(total)) * 100).toFixed(2)).toString();
        });
        return percentageSplit;
    }
    static fromPercentageSplit(totalSpend, split) {
        return this.toAmountSplit(totalSpend, split, 100);
    }
    static fromShareSplit(totalSpend, split) {
        const totalShares = Object.values(split).reduce((sum, shares) => sum + Number(shares), 0);
        return this.toAmountSplit(totalSpend, split, totalShares);
    }
    static toShareSplit(total, split) {
        const percentageSplit = this.toPercentageSplit(total, split);
        const gcd = this.findGCD(Object.values(percentageSplit).map(Number));
        return Object.fromEntries(Object.entries(percentageSplit).map(([id, shares]) => [
            id,
            gcd
                ? Number((Number(shares) / gcd).toFixed(2)).toString()
                : Number(shares).toString(),
        ]));
    }
    static autoCalculateSplit({ split, unEditedUsers, editQueue, max, splitType, }) {
        let newSplit = split;
        if (unEditedUsers?.length) {
            newSplit = this.autoCalculateEqualSplit({
                split,
                unEditedUsers,
                max,
                splitType,
            });
        }
        return this.autoCalculateQueueSplit({
            split: newSplit,
            editQueue,
            max,
            splitType,
        });
    }
    static autoCalculateEqualSplit({ split, unEditedUsers, max, splitType, }) {
        const currentTotal = Object.values(split).reduce((prev, amount) => prev + Number(amount), 0);
        const difference = Number((max - currentTotal).toFixed(2));
        const unEditedUsersTotal = Object.entries(split).reduce((prev, [userId, amount]) => {
            if (unEditedUsers.includes(Number(userId))) {
                return prev + Number(amount);
            }
            return prev;
        }, 0);
        const newValue = unEditedUsersTotal + difference;
        let newSplit = {};
        if (newValue > 0) {
            newSplit = this.toEqualSplit(newValue, unEditedUsers, unEditedUsers);
        }
        else {
            newSplit = unEditedUsers.reduce((acc, userId) => ({
                ...acc,
                [userId]: '0.00',
            }), {});
        }
        if (splitType === CustomSplitType.PERCENTAGE) {
            newSplit = Object.fromEntries(Object.entries(newSplit).map(([id, shares]) => [
                id,
                Number(shares).toString(),
            ]));
        }
        return {
            ...split,
            ...newSplit,
        };
    }
    static autoCalculateQueueSplit({ split, editQueue, max, splitType, }) {
        const currentTotal = Object.values(split).reduce((prev, amount) => prev + Number(amount), 0);
        let difference = Number((max - currentTotal).toFixed(2));
        const newSplit = { ...split };
        let i = 0;
        while (difference !== 0 && i < editQueue.current.length) {
            const userId = editQueue.current[i];
            const currentSplitForUser = Number(newSplit[userId]);
            let amountAdded = difference;
            let newAmount = Number((currentSplitForUser + difference).toFixed(2));
            if (newAmount < 0) {
                newAmount = 0;
                amountAdded = -currentSplitForUser;
            }
            if (newAmount > max) {
                newAmount = max;
                amountAdded = max - currentSplitForUser;
            }
            newSplit[userId] =
                splitType === CustomSplitType.AMOUNT
                    ? newAmount.toFixed(2)
                    : newAmount.toString();
            difference -= amountAdded;
            i += 1;
        }
        return newSplit;
    }
    static toAmountSplit(totalSpend, split, totalShares) {
        let remainingAmount = Math.abs(totalSpend);
        const amountSplit = {};
        const selectedIds = Object.entries(split)
            .map(([userId, share]) => (Number(share) > 0 ? Number(userId) : null))
            .filter(Boolean);
        Object.entries(split).forEach(([userId, shares]) => {
            const amount = Math.abs(totalSpend) * (Number(shares) / totalShares);
            amountSplit[Number(userId)] = !totalShares
                ? 0
                : Math.round(amount * 100) / 100;
            remainingAmount -= amountSplit[Number(userId)];
        });
        const sortedUserIds = Object.keys(split)
            .map(Number)
            .filter((id) => selectedIds.includes(id))
            .sort((a, b) => Number(split[Number(b)]) - Number(split[Number(a)]));
        let remainingCents = Math.round(remainingAmount * 100);
        for (let i = 0; remainingCents > 0 && i < sortedUserIds.length; i++) {
            amountSplit[sortedUserIds[i]] += 0.01;
            remainingCents -= 1;
        }
        for (let i = sortedUserIds.length - 1; remainingCents < 0 && i >= 0; i--) {
            amountSplit[sortedUserIds[i]] -= 0.01;
            remainingCents += 1;
        }
        return Object.fromEntries(Object.entries(amountSplit).map(([id, shares]) => [id, shares.toFixed(2)]));
    }
    static gcd(a, b) {
        if (a === 0)
            return b;
        if (a.toFixed(5) === '0.00000')
            return null;
        return this.gcd(b % a, a);
    }
    static findGCD(array) {
        const arr = array.filter(Boolean);
        let res = arr[0];
        for (let i = 1; i < arr.length; i++) {
            if (!res)
                return null;
            res = this.gcd(arr[i], res);
            if (res === 1)
                return 1;
        }
        return res;
    }
    static toNumber(split) {
        return Object.fromEntries(Object.entries(split).map(([id, shares]) => [id, Number(shares)]));
    }
    static toConverted(split, conversionRate) {
        return Object.fromEntries(Object.entries(this.toNumber(split)).map(([id, shares]) => [
            id,
            shares * conversionRate,
        ]));
    }
    static toString(split) {
        return Object.fromEntries(Object.entries(split).map(([id, shares]) => [id, shares.toFixed(2)]));
    }
}
